<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import store from '../../../../store/store';
import SingleText from './Fields/SingleText';
import SingleTextarea from './Fields/SingleTextarea';
import WYSIWYG from '@/views/Admin/Config/Population/Fields/WYSIWYG';
import GroupInput from '@/views/Admin/Config/Population/Fields/Groups/GroupInput';

export default {
    components: { WYSIWYG, SingleTextarea, SingleText, GroupInput },
    computed: {
        creating() {
            return this.$route.name === 'PopulationCreate';
        },

        fieldId: {
            get: function() {
                return store.state.populationEditor.fieldId;
            },
            set: function(value) {
                this.setFieldId(value);
            }
        },

        profession: {
            get: function() {
                return store.state.populationEditor.profession;
            },
            set: function(value) {
                this.setProfession(value);
            }
        },

        published: {
            get: function() {
                return store.state.populationEditor.published;
            },
            set: function(value) {
                this.setPublished(value);
            }
        },

        ...mapState('populationEditor', [
            'fields',
            'fieldsForSelect',
            'fieldType',
            'fieldOptions',
            'deleteModal',
            'hydrated',
            'populations'
        ]),

        ...mapGetters({
            publishedText: 'populationEditor/publishedText',
            selectableProfessions: 'populationEditor/selectableProfessions'
        })
    },

    watch: {
        fieldId() {
            this.setupField();
        }
    },

    destroyed() {
        this.setFieldId(null);
        this.setHydrated(false);
    },

    methods: {
        ...mapActions({
            showNotification: 'notification/show',
            saveData: 'populationEditor/saveData',
            destroyData: 'populationEditor/destroyData'
        }),

        ...mapMutations({
            toggleOverlay: 'overlay/toggle',
            setFieldId: 'populationEditor/setFieldId',
            setProfession: 'populationEditor/setProfession',
            setPublished: 'populationEditor/setPublished',
            setFieldName: 'populationEditor/setFieldName',
            setFieldType: 'populationEditor/setFieldType',
            setFieldOptions: 'populationEditor/setFieldOptions',
            toggleDeleteModal: 'populationEditor/toggleDeleteModal',
            setHydrated: 'populationEditor/setHydrated'
        }),

        save() {
            this.saveData();
        },

        destroy() {
            this.toggleDeleteModal(false);
            this.destroyData();
        },

        setupField() {
            this.setHydrated(false);
            const field = this.fields.find(field => field.id === this.fieldId);
            this.setFieldName(field.name);
            this.setFieldType(field.type);
            this.setFieldOptions(field.options);
            this.setFieldId(this.fieldId);
            this.setHydrated(true);
        }
    }
};
</script>

<template>
    <ValidationObserver v-slot="{ invalid }">
        <v-card :class="{ jsCardHasError: invalid }">
            <v-card-title>
                <v-switch
                    v-model="published"
                    :label="publishedText"
                />
            </v-card-title>
            <v-card-text>
                <form
                    action=""
                    method="POST"
                    @submit.prevent="save"
                >
                    <!-- Fields -->
                    <v-row>
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="fieldId"
                                :items="fieldsForSelect"
                                label="Field"
                                item-text="name"
                                item-value="id"
                                outlined
                            >
                                <template #item="data">
                                    <template>
                                        <v-list-item-content>
                                            <v-list-item-title v-html="data.item.name" />
                                            <v-list-item-subtitle
                                                v-html="data.item.group"
                                            />
                                        </v-list-item-content>
                                    </template>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>

                    <!-- Professions -->
                    <v-row v-if="fieldId">
                        <v-col cols="12">
                            <ValidationProvider name="profession">
                                <v-autocomplete
                                    :items="selectableProfessions"
                                    item-text="name"
                                    item-value="id"
                                    name="profession_id"
                                    id="profession_id"
                                    label="Specialty"
                                    v-model="profession"
                                    outlined
                                    no-data-text="All specialties for the selected field have been populated."
                                />
                            </ValidationProvider>
                        </v-col>
                    </v-row>

                    <!--
                    -------------------------------------------------
                    Population Editors
                    -------------------------------------------------
                    -->
                    <v-row v-if="hydrated">
                        <v-col cols="12">
                            <SingleText v-if="fieldType === 'text'" />
                            <SingleTextarea v-else-if="fieldType === 'textarea'" />
                            <WYSIWYG v-else-if="fieldType === 'wysiwyg'" />
                            <div v-else-if="fieldType === 'group'">
                                <GroupInput />
                            </div>
                            <div v-else>
                                {{
                                    fieldId ? 'This field does not support populations' : 'Please select a field to continue.'
                                }}
                            </div>
                        </v-col>
                    </v-row>
                </form>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    v-if="!creating"
                    text
                    @click="toggleDeleteModal(true)"
                    :disabled="invalid"
                >
                    Delete Population
                </v-btn>
                <v-spacer />
                <v-btn
                    :disabled="!profession || !fieldId"
                    type="submit"
                    color="primary"
                    @click="save"
                >
                    <v-icon
                        left
                        small
                    >
                        fas fa-save
                    </v-icon>
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </ValidationObserver>
</template>